import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { useFormStyles } from "../../hooks/useFormStyles";
import { EntityForm, getEntityFormSchema } from "../../types/forms/entity";
import { Entity, UpdateEntityRequest } from "../../types/model/entity/Entity";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";

interface EntityInfoProps {
  entityData: Entity;
  onSubmit: (values: UpdateEntityRequest) => Promise<void>;
  isUpdating?: boolean;
}

export default function EntityInfo ({ entityData, onSubmit, isUpdating }: Readonly<EntityInfoProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    canEditEntity(entityData.id);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getEntityFormSchema(t);
  const methods = useForm<EntityForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...entityData,
      fiscalNumber: entityData.fiscalNumber ?? "",
      website: entityData.website ?? "",
      notes: entityData.notes ?? "",
    },
  });

  useEffect(() => {
    methods.reset({
      ...entityData,
      fiscalNumber: entityData.fiscalNumber ?? "",
      website: entityData.website ?? "",
      notes: entityData.notes ?? "",
    });
  }, [entityData, methods]);

  const handleFormSubmit = async (values: EntityForm) => {
    try {
      setIsSaving(true);
      const normalizedValues: UpdateEntityRequest = {
        ...values,
        fiscalNumber: values.fiscalNumber?.trim() || null,
        website: values.website?.trim() || null,
        notes: values.notes?.trim() || null,
      };

      await onSubmit(normalizedValues);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Grid2 size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h5">
                      {t("entities.entity.general.information")}
                    </Typography>

                    {/* Action Icons */}
                    {isEditing && canEdit ? ( 
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("generic.save")}>
                          <IconButton
                            color="success"
                            onClick={methods.handleSubmit(handleFormSubmit)}
                            disabled={isSaving || isUpdating}
                          >
                            {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("generic.cancel")}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setIsEditing(false);
                              methods.reset();
                            }}
                            disabled={isSaving || isUpdating}
                          >
                            <EditOffIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      canEdit && (
                        <Tooltip title={t("generic.edit")}>
                          <IconButton
                            color="primary"
                            onClick={() => setIsEditing(true)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </Box>
                </Grid2>
                <Grid2 container spacing={2} mt={1}>
                  <Grid2 size={{ xs: 18, sm: 9 }}>
                    <TextField
                      fullWidth
                      label={`${t("entities.entity.general.name")} *`}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("name")}
                      error={!!methods.formState.errors.name}
                      helperText={methods.formState.errors.name?.message}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <TextField
                      fullWidth
                      label={`${t("entities.entity.general.acronym")} *`}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("acronym")}
                      error={!!methods.formState.errors.acronym}
                      helperText={methods.formState.errors.acronym?.message}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={`${t("entities.entity.general.fiscalNumber")} *`}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("fiscalNumber")} 
                      error={!!methods.formState.errors.fiscalNumber}
                      helperText={methods.formState.errors.fiscalNumber?.message}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t("entities.entity.general.website")}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("website")}
                      error={!!methods.formState.errors.website}
                      helperText={methods.formState.errors.website?.message}
                      />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label={t("entities.entity.general.notes")}
                      placeholder={t("entities.entity.general.notes-placeholder")}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      multiline
                      minRows={6}
                      sx={{ ...formStyles }}
                      {...methods.register("notes")}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
